import {Component, effect, Injector, model, OnInit, signal} from '@angular/core';
import {Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {RestApiService} from '../../services/api/rest-api.service';
import {SessionStorageService} from '../../services/session-storage.service';
import {Translations} from '../../services/translations.service';
import {CheckboxComponent} from '../../components/form/checkbox/checkbox.component';
import {ToggleButton} from 'primeng/togglebutton';
import {FormsModule} from '@angular/forms';
import {DatetimeComponent} from '../../components/form/datetime/datetime.component';
import {InputText} from 'primeng/inputtext';
import {Button} from 'primeng/button';
import {Routing} from '../../models/routing/routing';
import {TableLazyLoadEvent, TableModule} from 'primeng/table';
import {ListedResponse} from '../../models/hydratator/listed-response';
import {Movie} from '../../models/vision/movie';
import {PascalCasePipe} from '../../pipes/pascal-case.pipe';
import {StringifierService} from '../../services/stringifier.service';
import {FilterMoviesRequest} from '../../models/requests/vision/filter-movies.request';
import {DatePipe} from '@angular/common';
import {DropdownComponent} from '../../components/form/dropdown/dropdown.component';
import {ConfirmationService} from 'primeng/api';
import {ToastService} from '../../services/toast.service';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {downloadAttachment} from '../../utils/download-utils';
import {BlockableDivComponent} from '../../components/form/blockable-div/blockable-div.component';
import {BlockUI} from 'primeng/blockui';
import {WindowLabelPipe} from '../../pipes/window-label.pipe';

@UntilDestroy()
@Component({
  templateUrl: './dashboard.component.html',
  imports: [
    CheckboxComponent,
    ToggleButton,
    FormsModule,
    DatetimeComponent,
    InputText,
    Button,
    TableModule,
    PascalCasePipe,
    DatePipe,
    DropdownComponent,
    BlockableDivComponent,
    BlockUI,
    WindowLabelPipe
  ],
  styleUrls: ['./dashboard.component.scss'],
  animations: [
    trigger('slideInOut', [
      state('in', style({height: '*', opacity: 1})),
      state('out', style({height: '0px', opacity: 0})),
      transition('out => in', [
        style({height: '*', opacity: 1}),
        animate('300ms ease-out')
      ]),
      transition('in => out', [
        animate('300ms ease-in', style({height: '0px', opacity: 0}))
      ])
    ])
  ]
})
export class DashboardComponent implements OnInit {

  movieStrigifier: Movie;
  showAvailabilityColumn = true;

  filter = signal<FilterMoviesRequest>({
    pay1: true,
    pay2: true,
    pay3: true,
    svod: true,
    freeTv: true,
    libraryFreeTv: true,
    librarySvod: true,

    reserved: true,
    available: true,

    timeFrom: undefined,
    timeTo: undefined,

    searchText: '',

    from: 0,
    amount: 20,
    sortingKey: 'CreatedAt',
    sortingDirectionAsc: false,
  });
  loading = false;
  openedMovies: Record<number, boolean> = {};

  movies: ListedResponse<Movie> = <ListedResponse<Movie>>{
    dataList: [],
    count: 0,
    sequentialRequestId: 0
  };
  publishedItems: { label: string, value: string }[] = [{
    label: 'Pubblicato',
    value: 'true',
  }, {
    label: 'Bozza',
    value: 'false',
  }];

  getModelProp<K extends keyof FilterMoviesRequest>(key: K): FilterMoviesRequest[K] {
    return this.filter()[key];
  }

  setModelProp<K extends keyof FilterMoviesRequest>(key: K, value: FilterMoviesRequest[K]) {
    this.filter.update(f => ({...f, [key]: value}));
  }


  constructor(private readonly api: RestApiService,
              private readonly sessionService: SessionStorageService,
              public readonly translation: Translations,
              private readonly injector: Injector,
              private readonly router: Router,
              private readonly strigifier: StringifierService,
              private readonly confirmationService: ConfirmationService,
              private readonly toastService: ToastService,
  ) {
    this.movieStrigifier = this.strigifier.getStringifier(Movie);

    effect(async () => {
      const filter = this.filter();
      await this.load();
    });
  }


  ngOnInit() {
    this.load();
  }

  async load(event: TableLazyLoadEvent | undefined = undefined) {
    if (event) {
      const sortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;

      this.filter.update(filter => ({
        ...filter,
        from: event.first ?? 0,
        amount: event.rows ?? filter.amount,
        ...(sortField ? {
          sortingKey: sortField,
          sortingDirectionAsc: (event.sortOrder ?? -1) > 0
        } : {})
      }));
      return;
    }

    try {
      this.loading = true;
      const response = await this.api.movies.filter(this.filter());
      this.movies = response.data;
      for (const movie of this.movies.dataList) {
        // Initialize openedMovies with false for each movie
        if (!this.openedMovies[movie.movieId]) {
          this.openedMovies[movie.movieId] = false;
        }
      }
    } finally {
      this.loading = false;
    }
  }

  obj(item: any): Movie {
    return item;
  }

  createNew() {
    this.router.navigateByUrl(Routing.movies.new(true));
  }

  edit(movieId: number) {
    this.router.navigateByUrl(Routing.movies.edit(movieId));
  }

  updatePublished(movieId: number, published: boolean) {
    // show a confirmation dialog before updating
    this.confirmationService.confirm({
      header: 'Conferma modifica stato di pubblicazione',
      message: `Sei sicuro di voler ${published ? 'pubblicare' : 'rendere bozza'} questo film?`,
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      acceptButtonStyleClass: 'p-button-success',
      rejectButtonStyleClass: 'p-button-secondary p-button-text',
      acceptLabel: published ? 'Pubblica' : 'Salva come bozza',
      rejectLabel: 'Annulla',
      accept: async () => {
        try {
          await this.api.movies.updatePublished(movieId, {data: published});
          this.toastService.success('Successo', `Il film è stato ${published ? 'pubblicato' : 'salvato come bozza'}`);
          await this.load();
        } catch (error) {
          this.toastService.error('Errore', 'Errore durante l\'aggiornamento dello stato di pubblicazione');
        }
      },
      reject: async () => {
        await this.load();
      }
    });
  }

  toggleDetails(movie: Movie) {
    if (this.openedMovies[movie.movieId]) {
      this.openedMovies[movie.movieId] = false;
    } else {
      this.openedMovies[movie.movieId] = true;
    }
  }

  async downloadExcel() {
    try {
      this.loading = true;
      await downloadAttachment(this.api.catalogue.downloadExcel(), 'Catalogo_Vision_ADMIN.xlsx');
    } finally {
      this.loading = false;
    }
  }

  async downloadPdf() {
    try {
      this.loading = true;
      await downloadAttachment(this.api.catalogue.downloadPdf(), 'Catalogo_Vision.pdf');
    } finally {
      this.loading = false;
    }
  }
}
